import React, { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
    AddressFormAutocomplete,
    AddressFormTelInput,
} from "src/components/address";

import {
    Button,
    TextField,
    CircularProgress,
    Grid,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Paper,
    PaperProps
} from "@mui/material";
import { useState } from "react";
import { CountryEnum, StateEnum } from "src/common/enum";
import { useDataProvider, useRefresh } from "react-admin";
import Transformer from "src/common/Transformer";
import { AddressProps } from "src/resources/sales/interface";
import { useForm, useWatch } from "react-hook-form";
import { phonePattern } from "src/common/UtilFunctions";
import Draggable from 'react-draggable';

interface AddressFromModalProps {
    address: AddressProps;
    open: boolean;
    setOpen: any;
    title: string;
    path: string;
}

const PaperComponent = (props: PaperProps) => {
    return (
        <Draggable
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} />
        </Draggable>
    );
}

export const AddressFormModal = (props: AddressFromModalProps) => {
    const { address, open, setOpen, title, path } = props;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { register, trigger, control, handleSubmit, getValues, setValue, formState: { errors }} = useForm<AddressProps>({
        defaultValues: address
    });
    const dataProvider = useDataProvider();
    const refresh = useRefresh();

    const country = useWatch({control, name: 'country' });

    useEffect(() => {
        setValue('state', '');
    }, [country]);

    const transform = (data) => {
        Transformer(data, {
            remove: ["createdAt", "id", "updatedAt"],
            update: {},
        });
        return data;
    };

    const update = async (data: AddressProps) => {
        try {
            let formData = {...data};

            setLoading(true);
            setError(null);
            dataProvider
            .update(path, {
                id: "",
                data: transform({...formData}),
                previousData: address,
            })
            .then((res) => {
                setLoading(false);
                refresh();
                setOpen(false);
            })
            .catch((error) => {
                setError(error.message);
                setLoading(false);
            });
        } catch (e) {
            console.log(e);
        }
    }

    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            fullWidth={true}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(update)}>
            <DialogTitle sx={{padding: "18px"}}>
                <Typography variant="body1" sx={{fontSize: "20px", fontWeight: "500"}}>{title}</Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{padding: "18px !important"}}>
                <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="First Name"
                            variant="outlined"
                            size="small"
                            error={!!errors.first_name}
                            required
                            helperText={errors.first_name?.message}
                            {...register("first_name", {
                                required: "Field is required"
                            })}
                        />
                    </Grid> 
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Last Name"
                            variant="outlined"
                            size="small"
                            error={!!errors.last_name}
                            required
                            helperText={errors.last_name?.message}
                            {...register("last_name", {
                                required: "Field is required"
                            })}
                        />
                    </Grid> 
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Email"
                            variant="outlined"
                            size="small"
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            {...register("email", {
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: "Entered value does not match email format"
                                }
                            })}
                        />
                    </Grid> 
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Company"
                            variant="outlined"
                            size="small"
                            error={!!errors.company}
                            helperText={errors.company?.message}
                            {...register("company", {
                                required: "Field is required"
                            })}
                        />
                    </Grid>
                    <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Street Address"
                            variant="outlined"
                            size="small"
                            error={!!errors.street_address}
                            required
                            helperText={errors.street_address?.message}
                            {...register("street_address", {
                                required: "Field is required"
                            })}
                        />
                    </Grid> 
                    <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="City"
                            variant="outlined"
                            size="small"
                            error={!!errors.city}
                            required
                            helperText={errors.city?.message}
                            {...register("city", {
                                required: "Field is required"
                            })}
                        />
                    </Grid> 
                    <Grid item sm={12} md={4} sx={{marginBottom: "18px"}}>
                        <TextField
                            label="Zip"
                            variant="outlined"
                            size="small"
                            error={!!errors.zip}
                            required
                            helperText={errors.zip?.message}
                            {...register("zip", {
                                required: "Field is required"
                            })}
                        />
                    </Grid>
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <AddressFormAutocomplete
                            value={getValues("country")}
                            label={"Country"}
                            size="small"
                            required
                            error={!!errors.country}
                            helperText={errors.country?.message}
                            options={CountryEnum}
                            {...register("country", {
                                required: "Field is required",
                            })}
                            onChange={(e, value) => {
                                setValue('country', value.id, { shouldValidate: true });
                                if (value.id !== "US") {
                                    setValue('state', '', { shouldValidate: true });
                                }
                            }}
                        />
                    </Grid> 
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        { country == 'US' ?
                            <AddressFormAutocomplete
                                error={!!errors.state}
                                helperText={(errors.state as any)?.message}
                                {...register("state")}
                                label={"State"}
                                onChange={(e, value) =>
                                value === null ? "" : setValue("state", value.id, { shouldValidate: true })
                                }
                                size="small"
                                options={StateEnum}
                                value={getValues("state") || address.state}
                            />
                        :
                            <TextField
                                label="State / Region"
                                variant="outlined"
                                size="small"
                                error={!!errors.state}
                                helperText={(errors.state as any)?.message}
                                {...register("state", {
                                required: false
                                })}
                                onChange={(e) => setValue("state", e.target?.value, { shouldValidate: true })}
                                value={getValues("state") || address.state}
                            />
                        }
                    </Grid>

                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <AddressFormTelInput
                            value={getValues("phone")}
                            label="Phone"
                            error={!!errors.phone}
                            helperText={errors.phone?.message}
                            required
                            {...register("phone", {
                                required: "Field is required",
                                pattern: {
                                    value: phonePattern,
                                    message: "Entered value does not match phone format"
                                }
                            })}
                            onChange={(value) => {
                                setValue('phone', value, { shouldValidate: true });
                            }}
                        />
                    </Grid> 
                    <Grid item sm={12} md={6} sx={{marginBottom: "18px"}}>
                        <AddressFormTelInput
                            value={getValues("mobile")}
                            label="Mobile"
                            error={!!errors.mobile}
                            helperText={errors.mobile?.message}
                            {...register("mobile", {
                                pattern: {
                                    value: phonePattern,
                                    message: "Entered value does not match phone format"
                                }
                            })}
                            onChange={(value) => {
                                setValue('mobile', value, { shouldValidate: true });
                            }}
                        />
                    </Grid>

                    { error &&
                    <Grid item sm={12} md={12} sx={{display: "flex", marginTop: "18px"}}>
                        <Typography variant="subtitle2" color="error">{error}</Typography>
                    </Grid>                     
                    } 
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "18px"}}>
                <Button
                    variant="outlined"
                    onClick={() => {
                    setOpen(false);
                    }}
                >
                    Cancel
                </Button>
                <Button variant="contained" type="submit" disabled={loading}>
                    {loading && <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff", marginRight: "8px"}}/>}
                    Update
                </Button>
            </DialogActions>
            </form>
        </Dialog>
    );
};
